import { Navigate, Route, Routes } from 'react-router-dom';
import { ROLE_HOME, ROLES, TEACHING_ROLES } from '@/constants/roles';
import { ChangePasswordPage } from '@/features/auth/pages/ChangePasswordPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { SetPasswordPage } from '@/features/auth/pages/SetPasswordPage';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { PlatformDashboardPage } from '@/features/platform/pages/PlatformDashboardPage';
import { CreateInstitutePage } from '@/features/platform/institutes/pages/CreateInstitutePage';
import { EditInstitutePage } from '@/features/platform/institutes/pages/EditInstitutePage';
import { InstituteDetailPage } from '@/features/platform/institutes/pages/InstituteDetailPage';
import { InstituteSettingsPage } from '@/features/platform/institutes/pages/InstituteSettingsPage';
import { InstituteSubscriptionPage } from '@/features/platform/institutes/pages/InstituteSubscriptionPage';
import { InstitutesListPage } from '@/features/platform/institutes/pages/InstitutesListPage';
import { FeatureCataloguePage } from '@/features/platform/features/pages/FeatureCataloguePage';
import { InstituteReportPage } from '@/features/platform/reports/pages/InstituteReportPage';
import { FeatureTrackerPage } from '@/features/platform/features/pages/FeatureTrackerPage';
import { AcademicCalendarPage } from '@/features/institute/calendar/pages/AcademicCalendarPage';
import { StaffAttendancePage } from '@/features/institute/staffAttendance/pages/StaffAttendancePage';
import { InstituteTimetablePage } from '@/features/institute/timetable/pages/InstituteTimetablePage';
import { TransportPage } from '@/features/institute/transport/pages/TransportPage';
import { ClassesPage } from '@/features/institute/classes/pages/ClassesPage';
import { ClassSetupPage } from '@/features/institute/classes/pages/ClassSetupPage';
import { SubjectsPage } from '@/features/institute/subjects/pages/SubjectsPage';
import { HousesPage } from '@/features/institute/houses/pages/HousesPage';
import { FeesPage } from '@/features/institute/fees/pages/FeesPage';
import { PayHeadsPage } from '@/features/institute/payroll/pages/PayHeadsPage';
import { StaffPage } from '@/features/institute/staff/pages/StaffPage';
import { StudentFormPage } from '@/features/institute/students/pages/StudentFormPage';
import { StudentsPage } from '@/features/institute/students/pages/StudentsPage';
import { InstituteNoticesPage } from '@/features/institute/notices/pages/InstituteNoticesPage';
import { SupportTicketPage } from '@/features/institute/support/pages/SupportTicketPage';
import { SupportTicketsPage } from '@/features/institute/support/pages/SupportTicketsPage';
import { SubscriptionTransactionsPage } from '@/features/platform/subscriptions/pages/SubscriptionTransactionsPage';
import { SupportQueuePage } from '@/features/platform/support/pages/SupportQueuePage';
import { SupportTicketWorkspacePage } from '@/features/platform/support/pages/SupportTicketWorkspacePage';
import { NotificationsPage } from '@/features/institute/notifications/pages/NotificationsPage';
import { InstituteDashboardPage } from '@/features/institute/pages/InstituteDashboardPage';
import { InstituteProfilePage } from '@/features/institute/profile/pages/InstituteProfilePage';
import { AdminProfilePage } from '@/features/institute/account/pages/AdminProfilePage';
import { RolesPage } from '@/features/institute/roles/pages/RolesPage';
import { RoleSetupPage } from '@/features/institute/roles/pages/RoleSetupPage';
import { StudentDashboardPage } from '@/features/student/pages/StudentDashboardPage';
import { ParentAttendancePage } from '@/features/parent/pages/ParentAttendancePage';
import { ParentDashboardPage } from '@/features/parent/pages/ParentDashboardPage';
import { ParentHomeworkPage } from '@/features/parent/pages/ParentHomeworkPage';
import { ParentRecordedClassesPage } from '@/features/parent/pages/ParentRecordedClassesPage';
import { ParentFeesPage } from '@/features/parent/pages/ParentFeesPage';
import { ParentProfilePage } from '@/features/parent/pages/ParentProfilePage';
import { ParentNoticesPage } from '@/features/parent/pages/ParentNoticesPage';
import { ParentCalendarPage } from '@/features/parent/pages/ParentCalendarPage';
import { ParentMessagesPage } from '@/features/parent/pages/ParentMessagesPage';
import { ParentResultsPage } from '@/features/parent/pages/ParentResultsPage';
import { ParentSectionPage } from '@/features/parent/pages/ParentSectionPage';
import { ParentTimetablePage } from '@/features/parent/pages/ParentTimetablePage';
import { TeacherActivityReviewPage } from '@/features/teacher/pages/TeacherActivityReviewPage';
import { TeacherExamMarksPage } from '@/features/teacher/pages/TeacherExamMarksPage';
import { TeacherClassResultsPage } from '@/features/teacher/pages/TeacherClassResultsPage';
import { TeacherHomeworkReviewPage } from '@/features/teacher/pages/TeacherHomeworkReviewPage';
import { TeacherLeaveRequestsPage } from '@/features/teacher/pages/TeacherLeaveRequestsPage';
import { TeacherFeeRemindersPage } from '@/features/teacher/pages/TeacherFeeRemindersPage';
import { TeacherNoticesPage } from '@/features/teacher/pages/TeacherNoticesPage';
import { TeacherCalendarPage } from '@/features/teacher/pages/TeacherCalendarPage';
import { TeacherDailyReportPage } from '@/features/teacher/pages/TeacherDailyReportPage';
import { TeacherDailyReportFormPage } from '@/features/teacher/pages/TeacherDailyReportFormPage';
import { TeacherRecordedClassPage } from '@/features/teacher/pages/TeacherRecordedClassPage';
import { TeacherTimetableDayPage } from '@/features/teacher/pages/TeacherTimetableDayPage';
import { TeacherProfilePage } from '@/features/teacher/pages/TeacherProfilePage';
import { AuthLayout } from '@/layouts/AuthLayout';
import { PlatformLayout } from '@/layouts/PlatformLayout';
import { InstituteLayout } from '@/layouts/InstituteLayout';
import { ParentLayout } from '@/layouts/ParentLayout';
import { TeacherLayout } from '@/layouts/TeacherLayout';
import { DashboardPage } from '@/pages/DashboardPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ProtectedRoute } from './ProtectedRoute';

/** Sends an already-signed-in user away from a login screen to their dashboard. */
function RedirectIfAuthenticated({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isBootstrapping } = useAuthStore();
  if (isBootstrapping) return null;
  if (isAuthenticated && user) return <Navigate to={ROLE_HOME[user.role]} replace />;
  return <>{children}</>;
}

/** Wraps an unauthenticated screen in the two-panel shell. */
function PublicScreen({ children }: { children: React.ReactNode }) {
  return (
    <RedirectIfAuthenticated>
      <AuthLayout>{children}</AuthLayout>
    </RedirectIfAuthenticated>
  );
}

/**
 * Institute roles that do not have a console of their own yet still land on the
 * placeholder dashboard. ADMIN has the real institute area below, and platform
 * roles have the console.
 */
const PLACEHOLDER_ROLE_PATHS = [...new Set(Object.values(ROLE_HOME))].filter(
  (path) => path !== '/platform' && path !== '/admin' && path !== '/parent',
);

export function AppRoutes() {
  const { user, isAuthenticated, isBootstrapping } = useAuthStore();

  return (
    <Routes>
      {/* ---------- public ---------- */}
      {/* One sign-in screen for everybody; the role decides where they land. */}
      <Route
        path="/login"
        element={
          <PublicScreen>
            <LoginPage />
          </PublicScreen>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicScreen>
            <ForgotPasswordPage />
          </PublicScreen>
        }
      />
      {/* Where both the invite link and a reset link land. */}
      <Route
        path="/set-password"
        element={
          <PublicScreen>
            <SetPasswordPage />
          </PublicScreen>
        }
      />

      {/* ---------- platform console (our own staff only) ---------- */}
      <Route
        element={<ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN, ROLES.PLATFORM_SUPPORT]} />}
      >
        <Route path="/platform" element={<PlatformLayout />}>
          <Route index element={<PlatformDashboardPage />} />
          <Route path="institutes" element={<InstitutesListPage />} />
          <Route path="institutes/new" element={<CreateInstitutePage />} />
          <Route path="institutes/:uuid" element={<InstituteDetailPage />} />
          <Route path="institutes/:uuid/edit" element={<EditInstitutePage />} />
          {/* Readable by support, saved only by a SUPER_ADMIN — the page and the
              endpoint both enforce it. */}
          <Route path="institutes/:uuid/settings" element={<InstituteSettingsPage />} />
          <Route path="institutes/:uuid/subscription" element={<InstituteSubscriptionPage />} />
          {/* Read-only; support may see it too. */}
          <Route path="institutes/:uuid/report" element={<InstituteReportPage />} />
          {/* Readable by support, changed only by a SUPER_ADMIN — every write
              endpoint carries its own role check. */}
          <Route path="features" element={<FeatureTrackerPage />} />
          {/* Readable by support; add, edit and delete are SUPER_ADMIN only. */}
          <Route path="catalogue" element={<FeatureCataloguePage />} />
          {/* Read-only; support may see it too, as the API allows. */}
          <Route path="subscriptions" element={<SubscriptionTransactionsPage />} />
          {/* Both platform roles work the queue. */}
          <Route path="support" element={<SupportQueuePage />} />
          <Route path="support/:uuid" element={<SupportTicketWorkspacePage />} />
        </Route>
      </Route>

      {/* ---------- an institute administering itself ---------- */}
      {/* The role gate keeps other institutes' roles out; each screen then checks
          the permission its own endpoint requires. */}
      <Route element={<ProtectedRoute allowedRoles={[ROLES.ADMIN]} />}>
        <Route path="/admin" element={<InstituteLayout />}>
          <Route index element={<InstituteDashboardPage />} />
          <Route path="institute" element={<InstituteProfilePage />} />
          {/* The administrator's own account, not the school's — reached from
              their name in the top bar rather than the menu. */}
          <Route path="profile" element={<AdminProfilePage />} />
          <Route path="calendar" element={<AcademicCalendarPage />} />
          {/* Holidays are declared on the academic calendar now; old links land there. */}
          <Route path="holidays" element={<Navigate to="/admin/calendar" replace />} />
          {/* The institute's own board — what the office puts up for everybody.
              The class board is the teacher console's, and stays there. */}
          <Route path="notices" element={<InstituteNoticesPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="notifications/:id" element={<NotificationsPage />} />
          {/* Never wrapped in a feature gate: every school has Support. */}
          <Route path="support" element={<SupportTicketsPage />} />
          <Route path="support/:uuid" element={<SupportTicketPage />} />
          {/* The roster, and the setup screen that adds to it or corrects it.
              One component serves both jobs; the route is what tells it which.
              `new` is declared before `:uuid` for readability; the router ranks
              the static segment higher regardless. */}
          <Route path="classes" element={<ClassesPage />} />
          <Route path="classes/new" element={<ClassSetupPage />} />
          <Route path="classes/:uuid/edit" element={<ClassSetupPage />} />
          <Route path="subjects" element={<SubjectsPage />} />
          <Route path="houses" element={<HousesPage />} />
          <Route path="staff" element={<StaffPage />} />
          <Route path="staff-attendance" element={<StaffAttendancePage />} />
          <Route path="timetable" element={<InstituteTimetablePage />} />
          {/* Transport. Laid out against static data while the module is built —
              see the page's own note. */}
          <Route path="transport" element={<TransportPage />} />

          {/* Students: the list, the new admission, and one child's record —
              all under the one path, because they are the one job. `new` is
              declared before `:uuid` for readability; the router ranks the
              static segment higher regardless. */}
          <Route path="students" element={<StudentsPage />} />
          <Route path="students/new" element={<StudentFormPage />} />
          <Route path="students/:uuid" element={<StudentFormPage />} />
          {/* Where admitting used to live. Kept so a bookmark or a link in an
              old email still lands somewhere. */}
          <Route path="admissions/new" element={<Navigate to="/admin/students/new" replace />} />
          {/* The fee counter — the register and the day book, one screen. It
              reads the same roll as Students above and works the same at a
              school and at a college. */}
          <Route path="fees" element={<FeesPage />} />
          {/* Pay heads now; the monthly run takes `/admin/payroll` itself next. */}
          <Route path="payroll" element={<Navigate to="/admin/payroll/settings" replace />} />
          <Route path="payroll/settings" element={<PayHeadsPage />} />
          {/* Creating and editing a role is its own screen, as the class
              setup is: thirty-eight permissions across ten areas is not a
              dialog's worth of decision. */}
          <Route path="roles" element={<RolesPage />} />
          <Route path="roles/new" element={<RoleSetupPage />} />
          <Route path="roles/:uuid/edit" element={<RoleSetupPage />} />
          <Route path="password" element={<ChangePasswordPage />} />
        </Route>
      </Route>

      {/* ---------- a parent reading their own child's school ---------- */}
      {/* Every address in the panel is under `/parent`, so it is an area of the
          product the way `/admin` and `/teacher` are and `isPathForRole` can
          send anybody else back to their own. The role gate is what keeps a
          parent's panel a parent's; which *children* they may read is decided
          by the server from their token, never by an id in the address. */}
      <Route element={<ProtectedRoute allowedRoles={[ROLES.PARENT]} />}>
        <Route path="/parent" element={<ParentLayout />}>
          {/* Every section on the menu lands somewhere. The ones without a
              screen of their own yet draw `ParentSectionPage`, and each is
              swapped for its own page as it is built. */}
          <Route index element={<ParentDashboardPage />} />
          <Route path="attendance" element={<ParentAttendancePage />} />
          <Route path="homework" element={<ParentHomeworkPage />} />
          <Route path="timetable" element={<ParentTimetablePage />} />
          <Route path="results" element={<ParentResultsPage />} />
          <Route path="recorded-classes" element={<ParentRecordedClassesPage />} />
          <Route path="notices" element={<ParentNoticesPage />} />
          <Route path="calendar" element={<ParentCalendarPage />} />
          <Route path="messages" element={<ParentMessagesPage />} />
          <Route path="transport" element={<ParentSectionPage />} />
          <Route path="fees" element={<ParentFeesPage />} />
          <Route path="profile" element={<ParentProfilePage />} />
        </Route>
      </Route>

      {/* ---------- student & other institute roles ---------- */}
      <Route element={<ProtectedRoute />}>
        {/* The student panel. One page, but every section of it has an address
            of its own, so a child can reload the screen they are on and link to
            it — the page reads which one from the path.

            Gated to STUDENT, like the platform and admin consoles above it.
            Without a role on this block any signed-in account could sit here,
            and an administrator who arrived from a stale `/student` address
            stayed on the child's panel instead of being sent to their own. */}
        <Route element={<ProtectedRoute allowedRoles={[ROLES.STUDENT]} />}>
          <Route path="/student" element={<StudentDashboardPage />} />
          <Route path="/student/timetable" element={<StudentDashboardPage />} />
          <Route path="/student/homework" element={<StudentDashboardPage />} />
          <Route path="/student/notices" element={<StudentDashboardPage />} />
          <Route path="/student/calendar" element={<StudentDashboardPage />} />
          <Route path="/student/lectures" element={<StudentDashboardPage />} />
          <Route path="/student/marks" element={<StudentDashboardPage />} />
          <Route path="/student/leave-requests" element={<StudentDashboardPage />} />
          <Route path="/student/fees" element={<StudentDashboardPage />} />
          <Route path="/student/profile" element={<StudentDashboardPage />} />
          <Route path="/student/settings" element={<Navigate to="/student/profile?tab=settings" replace />} />
        </Route>

        {/* Teacher Console Routes.
            One shell around all of them, the same one the institute and
            platform consoles use. The page still decides which section to draw
            from the path, so the sidebar link and the screen agree. */}
        {/* Teaching roles only — without this any signed-in account, a student
            or a parent included, could open the teacher console by its address. */}
        <Route element={<ProtectedRoute allowedRoles={TEACHING_ROLES} />}>
          <Route path="/teacher" element={<Navigate to="/teacher/dashboard" replace />} />
          <Route element={<TeacherLayout />}>
            <Route path="/teacher/dashboard" element={<DashboardPage />} />
            <Route path="/teacher/attendance" element={<DashboardPage />} />
            <Route path="/teacher/leave-requests" element={<TeacherLeaveRequestsPage />} />
            <Route path="/teacher/fee-reminders" element={<TeacherFeeRemindersPage />} />
            {/* One screen under two addresses: a school sets homework, a college
              sets assignments, and each is linked to by the name it uses. */}
            <Route path="/teacher/homework" element={<DashboardPage />} />
            <Route path="/teacher/assignments" element={<DashboardPage />} />
            <Route path="/teacher/activities" element={<DashboardPage />} />
            <Route path="/teacher/videos" element={<DashboardPage />} />
            <Route
              path="/teacher/recorded-classes/:recordingId"
              element={<TeacherRecordedClassPage />}
            />
            <Route
              path="/teacher/videos/:recordingId"
              element={<TeacherRecordedClassPage />}
            />
            <Route path="/teacher/timetable" element={<DashboardPage />} />
            <Route path="/teacher/marks" element={<DashboardPage />} />
            {/* The marks sheet for one paper — its own screen, because a sheet is
              a table of the whole class saved as one thing. Inside the console
              shell, so the sidebar still says which section you came from.

              Two addresses, and both kept on purpose: the papers table links to
              one spelling and the exam list to the other, so dropping either
              would break links already written against it. */}
            <Route path="/teacher/marks/:examId" element={<TeacherExamMarksPage />} />
            {/* The class teacher's results: every subject side by side. Before
              `:examId` would matter — it is a different segment count anyway. */}
            <Route path="/teacher/exams/results" element={<TeacherClassResultsPage />} />
            <Route path="/teacher/exams/:examId/marks" element={<TeacherExamMarksPage />} />
            <Route path="/teacher/notices" element={<TeacherNoticesPage />} />
            <Route path="/teacher/calendar" element={<TeacherCalendarPage />} />
            <Route path="/teacher/daily-report" element={<TeacherDailyReportPage />} />
            {/* Writing one class up. The class, subject and day are the address
              rather than state, so the form can be linked to and reloaded. */}
            <Route path="/teacher/daily-report/entry" element={<TeacherDailyReportFormPage />} />
            <Route
              path="/teacher/homework/:homeworkId/review"
              element={<TeacherHomeworkReviewPage />}
            />
            <Route path="/homework/:homeworkId/review" element={<TeacherHomeworkReviewPage />} />
            <Route
              path="/teacher/activities/:activityId/review"
              element={<TeacherActivityReviewPage />}
            />
            <Route path="/activities/:activityId/review" element={<TeacherActivityReviewPage />} />
            <Route path="/teacher/timetable/:date" element={<TeacherTimetableDayPage />} />
            <Route path="/teacher/profile" element={<TeacherProfilePage />} />
            <Route path="/teacher/settings" element={<TeacherProfilePage initialTab="settings" />} />
          </Route>
        </Route>

        {/* The roles with no console of their own land on the same placeholder,
            and inside the same shell — it is what they already saw, when that
            shell was drawn by the page itself. Without a layout here they would
            get the content with no navigation and nowhere to sign out. */}
        <Route element={<TeacherLayout />}>
          {PLACEHOLDER_ROLE_PATHS.filter((p) => p !== '/student' && !p.startsWith('/teacher')).map(
            (path) => (
              <Route key={path} path={path} element={<DashboardPage />} />
            ),
          )}
        </Route>
      </Route>

      {/* ---------- entry point ---------- */}
      <Route
        path="/"
        element={
          isBootstrapping ? null : isAuthenticated && user ? (
            <Navigate to={ROLE_HOME[user.role]} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
